<template>
    <div class="settings__page">
        <div>
            <div class="mb--35">
                <CustomText size="lg" color="gray-800" transform="capitalize" weight="semi--bold">
                    General Settings
                </CustomText>
            </div>

            <template v-if="pageLoading">
                <PageSpinner />
            </template>

            <template v-else>
                <div class="settings__row__item mt--0">
                    <div class="flex align-items-center w-100 justify-content-between">
                        <CustomText size="sm" color="text-gray-600" transform="capitalize" weight="bold">
                            Language & Currency
                        </CustomText>
                        <BaseButton
                            variant="outline" 
                            label="Edit"
                            variant-style="primary"
                            @submit="openModal('currency')"
                        />
                    </div>
                    
                    <div>
                        <p class="text--sm text-gray-600 mt--5">
                            <span>{{ general.language || 'English' }},</span>
                            <span class="text--upper ml--5">{{ general.currency || '-' }}</span>
                        </p>
                    </div>
                </div>
    
                <div class="settings__sub__header mt--0">
                    <div class="left">
                        <p class="text--semi--bold text-gray-700 text--sm">
                            Theme
                        </p>
                        <div>
                            <p class="text--sm text--capitalize text-gray-600 mt--5">
                                {{ appearance.theme }}
                            </p>
                        </div>
                    </div>
                    <div>
                        <BaseButton
                            variant-style="primary" 
                            variant="outline" 
                            label="Coming soon"
                            disabled
                        />
                    </div>
                </div>

                <div class="settings__sub__header mt--0">
                    <div>
                        <p class="text--semi--bold text-gray-700 text--sm">
                            Name Display Preference
                        </p>
                        <div class="grid gap-3 mt--15">
                            <div>
                                <FormLabel title="For payments (invoices & rate cards)" />
                                <CustomDropdown 
                                    id="industryDropdown" 
                                    v-model="form.nameDisplay.payments" 
                                    :options="nameDisplaySettings"
                                    size="md"
                                    index-label="name"
                                    btn-classes="text--normal" 
                                    list-item-class="text--capitalize" 
                                    list-type="industries" 
                                    @select="v => form.nameDisplay.payments = v" 
                                />
                            </div>
    
                            <div>
                                <FormLabel title="Public profile" />
                                <CustomDropdown 
                                    id="industryDropdown" 
                                    v-model="form.nameDisplay.public_profile" 
                                    :options="nameDisplaySettings"
                                    size="md"
                                    index-label="name"
                                    btn-classes="text--normal" 
                                    list-item-class="text--capitalize" 
                                    @select="v => form.nameDisplay.public_profile = v" 
                                />
                            </div>

                            <div>
                                <FormLabel title="Comms (sending emails to clients...etc)" />
                                <CustomDropdown 
                                    id="industryDropdown" 
                                    v-model="form.nameDisplay.comms" 
                                    :options="nameDisplaySettings"
                                    size="md"
                                    index-label="name"
                                    btn-classes="text--normal" 
                                    list-item-class="text--capitalize" 
                                    @select="v => form.nameDisplay.comms = v" 
                                />
                            </div>
                        </div>
                        <div class="mt--20 mb--10">
                            <BaseButton
                                variant-style="primary" 
                                variant="outline" 
                                label="Save"
                                :loading="nameSettingsLoading"
                                @submit="updateNameDisplaySettings"
                            />
                        </div>
                    </div>
                </div>
    
                <div class="mt--15">
                    <div class="mb--15 flex align-items-center w-100 justify-content-between">
                        <FormLabel 
                            title="Time settings" 
                            label-type="menu" 
                            size="sm" 
                            class="text--capitalize text--semi--bold" 
                        />
                        <BaseButton
                            variant-style="primary" 
                            variant="outline" 
                            label="Edit"
                            @submit="openModal('timeSettings')"
                        />
                    </div>

                    <div>
                        <div class="mb--15">
                            <FormLabel 
                                title="Timezone:" 
                                class="text--semi--bold" 
                            />
                            <p class="text--sm text-gray-600">
                                {{ general.timeZone || 'N/A' }}
                            </p>
                        </div>
                        <div class="mb--15">
                            <FormLabel 
                                title="Preferred Time Format:" 
                                class="text--semi--bold" 
                            />
                            <p class="text--sm text-gray-600">
                                {{ general.timeFormat || 'N/A' }}
                            </p>
                        </div>
                        <div class="mb--15">
                            <FormLabel 
                                title="Preferred Date Format:" 
                                class="text--semi--bold" 
                            />
                            <p class="text--sm text-gray-600 mt--10">
                                {{ general.dateFormat || 'N/A' }}
                            </p>
                        </div>
                        <div>
                            <FormLabel 
                                title=" Week starts from:" 
                                class="text--semi--bold" 
                            />
                        </div>
                        <p class="text--sm text-gray-600 mt--10 text--capitalize">
                            {{ general.dayOfWeek }}
                        </p>
                    </div>
                </div>
            </template>
        </div>

        <MainModal 
            :show-modal="showGeneralModal" 
            size="sm"  
            :title="title" 
            @close="closeModal"
        >
            <template #body>
                <form @submit.prevent> 
                    <div v-if="hasField('currency')" class="form__item">
                        <FormLabel id="currency" title="Currency" />
                        <CustomDropdown 
                            id="currencyDropdown"
                            v-model="form.currency" 
                            :options="settlementAccountCurrencies"
                            list-type="currencies"
                            @search="searchCurrencies" 
                            @select="v => form.currency = v" 
                        />
                    </div>

                    <div v-if="hasField('theme')" class="form__item">
                        <FormLabel html-for="theme" title="Theme color" />
                        <select v-model="form.theme" class="form-select form-select-sm text--sm filter-input">
                            <option v-for="item in theme" :key="item" :value="item">
                                {{ item }}
                            </option>
                        </select>
                    </div>

                    <div v-if="hasField('timeFormat')" class="form__item">
                        <div class="form__item">
                            <FormLabel id="timeFormat" title="Preferred Time Format" />
                            <select v-model="form.timeFormat" class="form-select form-select-sm text--sm filter-input">
                                <option v-for="(format, index) in ['12h', '24h']" :key="index" :value="format">
                                    {{ format }}
                                </option>
                            </select>
                        </div>
    
                        <div v-if="hasField('timeZone')" class="form__item">
                            <FormLabel id="timeZone" title="Time zone" />
                            <select v-model="form.timeZone" class="form-select form-select-sm text--sm filter-input">
                                <option v-for="(lang, index) in timeZones" :key="index" :value="lang">
                                    {{ lang }}
                                </option>
                            </select>
                        </div>
    
                        <div v-if="hasField('dateFormat')" class="form__item">
                            <FormLabel id="dateFormat" title="Preferred Date Format" />
                            <select v-model="form.dateFormat" class="form-select form-select-sm text--sm">
                                <option v-for="(item, index) in dateFormats" :key="index" :value="item">
                                    {{ item }}
                                </option>
                            </select>
                        </div>
    
                        <div v-if="hasField('dayOfWeek')" class="form__item">
                            <FormLabel id="dayOfWeek" title="Week starts from" />
                            <select v-model="form.dayOfWeek" class="form-select form-select-sm text--sm text--capitalize">
                                <option v-for="(day, index) in daysOfWeek" :key="index" :value="day">
                                    {{ day }}
                                </option>
                            </select>
                        </div>
                    </div>
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        label="Cancel" 
                        variant="outline" 
                        class="mr--5" 
                        @submit="closeModal"
                    />
                    <BaseButton label="Save" :loading="loading" @submit="handleUpdateSettings" />
                </div>
            </template>
        </MainModal>
    </div>
</template>

<script>
import {
    MainModal,
    FormLabel,
    BaseButton,
    PageSpinner, 
    CustomDropdown,
    CustomText,
} from "@/components/ui/index";
import { generalModalFields } from "@/components/dataComponents/auth";
import { dateFormats, daysOfWeek, theme } from "@/utils/dummy";
import { settlementAccountCurrencies } from "@/plugins/countries";

export default {
    name: "GeneralSettings",

    components: {
        FormLabel,
        BaseButton,
        MainModal,
        PageSpinner, 
        CustomDropdown,
        CustomText,
    },

    data() {
        return {
            settlementAccountCurrencies,
            pageLoading: false,
            loading: false,
            form: {
                timeZone: "",
                timeFormat: "",
                dateFormat: "",
                dayOfWeek: "Sunday",
                theme: "",
                language: "",
                currency: "",   
                nameDisplay: {
                    payments: "",
                    public_profile: "",
                    comms: "",
                },       
            },
            dateFormats,
            daysOfWeek,
            theme,
            showGeneralModal: false,
            generalModalFields,
            currentSelection: "",
            timeZones: Intl.supportedValuesOf("timeZone"),
            nameDisplaySettings: [
                { id: "legal_name", name: "Legal name" },
                { id: "brand_name", name: "Brand name" },
            ],
            nameSettingsLoading: false,
        };
    },

    computed: {
        general() {
            return this.$store.getters["settings/general"];
        },

        title() {
            return this.generalModalFields[this.currentSelection]?.title || "";
        },

        appearance() {
            return this.$store.getters["settings/appearance"];
        },

        userProfile() {
            return this.$store.getters["auth/profile"];
        },
    }, 

    watch: {
        currentSelection(newVal) {
            if (newVal) {
                if (this.generalModalFields[newVal] && this.generalModalFields[newVal]?.fields) {
                    this.populateFormBasedOnSelection(newVal);
                }
            }
        },
    },

    mounted() {
        if (this.general?.nameDisplay) {
            this.form = {
                ...this.form,
                nameDisplay: {
                    ...this.form.nameDisplay,
                    payments: this.nameDisplaySettings.find(x => x.id === this.general.nameDisplay?.payments),
                    comms: this.nameDisplaySettings.find(x => x.id === this.general.nameDisplay?.comms),
                    public_profile: this.nameDisplaySettings.find(x => x.id === this.general.nameDisplay?.public_profile),
                },
            };
        }
    },

    methods: {
        openModal(val) {
            if (!val || typeof val !== "string") return;

            this.currentSelection = val;
            this.showGeneralModal = true;
        },

        closeModal() {
            this.showGeneralModal = false;
            this.currentSelection = "";
        },

        setForm(data) {
            this.form = {
                timeZone: data?.timeZone || "",
                timeFormat: data?.timeFormat || "",
                dateFormat: data?.dateFormat || "",
                theme: data?.theme || "",
                dayOfWeek: data?.dayOfWeek || "Sunday",
                currency: data?.currency || "",    
            };            
        },

        async updateNameDisplaySettings() {
            try {
                if (!this.userProfile?.brandName && (
                    this.form.nameDisplay.public_profile?.id === "brand_name" || 
                    this.form.nameDisplay.payments?.id === "brand_name" || 
                    this.form.nameDisplay.comms?.id === "brand_name"
                )) {
                    this.$toast.error("Add a brand name to your account to update this setting");
                    return;
                }

                this.nameSettingsLoading = true;
                
                await this.$store.dispatch("settings/updateGeneralSettings", {
                    "type": "general",
                    settings: {
                        nameDisplay: {
                            public_profile: this.form.nameDisplay.public_profile?.id,
                            payments: this.form.nameDisplay.payments?.id,
                            comms: this.form.nameDisplay.comms?.id,
                        },
                    },
                });
                this.$toast.info("Settings updated");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.nameSettingsLoading = false;
            }
        },

        async handleUpdateSettings() {
            try {
                this.loading = true;
                const payload = {
                    type: this.currentSelection === "appearance" ? "appearance" : "general",
                    settings: {},
                };

                const selectedItem = this.generalModalFields[this.currentSelection]?.fields;

                for (const key in selectedItem) {
                    if (this.form.hasOwnProperty(key)) {
                        payload.settings[key] = this.form[key];
                    }
                }
                
                await this.$store.dispatch("settings/updateGeneralSettings", payload);
                this.$toast.info("Settings updated");
                this.closeModal();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loading = false;
            }
        },

        resetForm() {
            this.form = {
                timeZone: "",
                timeFormat: "",
                dateFormat: "",
                theme: "",
                dayOfWeek: "Sunday",
                language: "",
                currency: "",     
            };
        },

        hasField(field) {
            return !this.generalModalFields[this.currentSelection] ? false : this.generalModalFields[this.currentSelection].fields[field]; 
        },

        populateFormBasedOnSelection(val) {
            const selectedItem = this.generalModalFields[val]?.fields;

            for (const key in selectedItem) {
                if (this.general.hasOwnProperty(key)) {
                    this.form[key] = this.general[key];
                }

                if (this.appearance.hasOwnProperty(key)) {
                    this.form[key] = this.appearance[key];
                }
            }
        },

        searchCurrencies(val) {
            const initialList = settlementAccountCurrencies;

            if (val) {
                const filteredList = initialList.filter(item => item.toLowerCase().includes(val?.toLowerCase()));
                this.settlementAccountCurrencies = filteredList;
            } else {
                this.settlementAccountCurrencies = initialList;
            }
        },
    },
};
</script>